import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  Sparkles,
  Wand2,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { CfoPolicyEditor } from "./CfoPolicyEditor";
import { updateCfoConfiguration } from "@/api/cfoWorkflow";
import { DocumentedPathEditor } from "./DocumentedPathEditor";
import { getProject, updateProject } from "@/api/projects";
import { rescoreAllCompanies } from "@/api/companies";
import { useProjectStore } from "@/stores/project.store";
import { useScoringStore } from "@/stores/scoring.store";
import { usePermissions } from "@/hooks/usePermissions";
import {
  FOUNDER_ORIGIN_DEFAULT_SUB_SCORES,
  FOUNDER_ORIGIN_FLAT,
  HIRING_SIGNALS_DEFAULT_SUB_SCORES,
} from "@/lib/options";
import { SubScoresEditor } from "./SubScoresEditor";
import {
  TrancheConfigEditor,
  computeMaxPossibleScore,
  countTrancheCriteria,
} from "./TrancheConfigEditor";
import {
  getEffectiveScoringConfig,
  getEffectiveScoringExclusions,
  getEffectiveScoringRules,
  getEffectiveScoringThreshold,
} from "@/lib/projectConfig";
import type { Project, ScoringConfig, ScoringRule } from "@/api/types";
import { DimensionConfigEditor } from "./scoring/ScoringConfigEditors";
import { GlobalScoringConfig } from "./scoring/GlobalScoringConfig";

// Backend rule keys that map to the per-origin breakdown UI.
// "origine_fondateur" is the live key; "founder_origin" is kept as a fallback
// in case a project was seeded with the metadata-style key.
const FOUNDER_ORIGIN_RULE_KEYS = new Set(["origine_fondateur", "founder_origin"]);

/** Live key for the hiring-signals scoring rule. */
const HIRING_SIGNALS_RULE_KEY = "signaux_recrutement";

/** Rules that ship a sub_scores breakdown. The expand chevron and
 *  the per-rule editor only render for these. */
const SUB_SCORED_RULE_KEYS = new Set<string>([
  ...FOUNDER_ORIGIN_RULE_KEYS,
  HIRING_SIGNALS_RULE_KEY,
]);

/** Fallback seeds when the project hasn't shipped a sub_scores
 *  override yet. Indexed by rule key so the loader stays
 *  data-driven. */
const SUB_SCORE_DEFAULTS: Record<string, Record<string, number>> = {
  origine_fondateur: FOUNDER_ORIGIN_DEFAULT_SUB_SCORES,
  founder_origin: FOUNDER_ORIGIN_DEFAULT_SUB_SCORES,
  [HIRING_SIGNALS_RULE_KEY]: HIRING_SIGNALS_DEFAULT_SUB_SCORES,
};

/** A scoring-exclusion entry, kept as the raw object stored on the
 *  project so save round-trips every field verbatim. Two shapes exist:
 *  v1 `{field, equals}` and v2 `{key?, field?, label, value, enabled}`
 *  where `value` can be a string, boolean, or array. */
type ExclusionEntry = Record<string, unknown>;

/** Display labels for fields the operator may already know by name.
 *  Anything missing from this map is humanized from the field key
 *  (`legal_form` → `Legal Form`). Adding a new key here is purely
 *  cosmetic — the engine reads `field` / `equals` only. */
const EXCLUSION_LABELS: Record<string, string> = {
  founder_origin: "Founder origin",
  has_finance_lead: "Finance lead in place",
  legal_form: "Legal form",
};

function humanizeField(field: string): string {
  return (
    EXCLUSION_LABELS[field] ??
    field
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/** Render an exclusion's comparison value across the shapes v2 ships:
 *  string ("none"), boolean (true/false), array (["series_b", …]), or
 *  missing. */
function formatExclusionValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map((v) => String(v)).join(", ");
  return String(value);
}

/** Human-readable text for one exclusion. Prefers the v2 `label`; falls
 *  back to `field = value` (or just the value) for v1 / unlabeled rows. */
function exclusionDisplay(ex: ExclusionEntry): string {
  if (typeof ex.label === "string" && ex.label.trim()) return ex.label;
  const field =
    typeof ex.field === "string"
      ? ex.field
      : typeof ex.key === "string"
        ? ex.key
        : null;
  const value = "value" in ex ? ex.value : ex.equals;
  if (field) return `${humanizeField(field)} = ${formatExclusionValue(value)}`;
  return formatExclusionValue(value);
}

interface ScoringSectionProps {
  project: Project;
}

export function ScoringSection({ project }: ScoringSectionProps) {
  const canEditScoring = usePermissions().canEditScoring;
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const setProjects = useProjectStore((s) => s.setProjects);
  const projects = useProjectStore((s) => s.projects);

  const initialRules = useMemo<ScoringRule[]>(() => {
    const source = getEffectiveScoringRules(project);
    return (source as unknown as ScoringRule[]).map((r) => {
      const key = String(r.key);
      const max = Number(project.cfo_effective_scoring ? ((r as ScoringRule & { weight?: number }).weight ?? r.max_points) : r.max_points);
      const next: ScoringRule = {
        key,
        label: String(r.label),
        max_points: max,
      };
      if (project.cfo_effective_scoring && r.enabled !== undefined) next.enabled = r.enabled;
      if (SUB_SCORED_RULE_KEYS.has(key)) {
        const seed = (r.sub_scores ?? SUB_SCORE_DEFAULTS[key] ?? {}) as
          | Record<string, number>
          | null;
        next.sub_scores = clampSubScores(seed ?? {}, max);
      }
      // M&A rules ship a free-form `config` map (tranche tables,
      // scalar bonuses, keyed score maps). Carry it through as-is so
      // the TrancheConfigEditor below can edit it in place.
      if (r.config && typeof r.config === "object") {
        next.config = r.config;
      }
      return next;
    });
  }, [project]);

  const initialThreshold = getEffectiveScoringThreshold(project);

  const initialExclusions = useMemo<ExclusionEntry[]>(() => {
    // Keep each exclusion object verbatim (don't reduce to {field,
    // equals}) so v2 fields — label, value, enabled — survive the
    // state round-trip and get saved back intact.
    return getEffectiveScoringExclusions(project).filter(
      (ex): ex is Record<string, unknown> =>
        typeof ex === "object" && ex !== null && !Array.isArray(ex),
    );
  }, [project]);

  // CFO v2 dynamic scoring config (dimension breakdowns + keyword lists
  // + booster / stage-duration scalars). Empty {} for project types that
  // don't ship it, in which case the dimension panels and the advanced
  // sections below simply don't render.
  const initialScoringConfig = useMemo<ScoringConfig>(
    () => getEffectiveScoringConfig(project),
    [project],
  );

  const [rules, setRules] = useState<ScoringRule[]>(initialRules);
  const [threshold, setThreshold] = useState<number>(initialThreshold);
  const [exclusions, setExclusions] =
    useState<ExclusionEntry[]>(initialExclusions);
  const [scoringConfig, setScoringConfig] =
    useState<ScoringConfig>(initialScoringConfig);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [rescoreDialogOpen, setRescoreDialogOpen] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const setDirty = useScoringStore((s) => s.setDirty);

  // Re-hydrate when project changes externally.
  useEffect(() => {
    setRules(initialRules);
    setThreshold(initialThreshold);
    setExclusions(initialExclusions);
    setScoringConfig(initialScoringConfig);
  }, [initialRules, initialThreshold, initialExclusions, initialScoringConfig]);

  const total = useMemo(
    () => rules.reduce((sum, r) => sum + (r.max_points ?? 0), 0),
    [rules],
  );

  const isDirty = useMemo(() => {
    if (threshold !== initialThreshold) return true;
    if (rules.length !== initialRules.length) return true;
    for (let i = 0; i < rules.length; i++) {
      if (rules[i].max_points !== initialRules[i].max_points) return true;
      if (rules[i].enabled !== initialRules[i].enabled) return true;
      if (!subScoresEqual(rules[i].sub_scores, initialRules[i].sub_scores))
        return true;
      if (
        JSON.stringify(rules[i].config ?? null) !==
        JSON.stringify(initialRules[i].config ?? null)
      )
        return true;
    }
    if (JSON.stringify(exclusions) !== JSON.stringify(initialExclusions))
      return true;
    if (
      JSON.stringify(scoringConfig) !== JSON.stringify(initialScoringConfig)
    )
      return true;
    return false;
  }, [
    rules,
    threshold,
    exclusions,
    scoringConfig,
    initialRules,
    initialThreshold,
    initialExclusions,
    initialScoringConfig,
  ]);

  function updateDimensionConfig(
    dimKey: string,
    next: Record<string, unknown>,
  ) {
    setScoringConfig((prev) => ({ ...prev, [dimKey]: next }));
  }

  function updateRulePoints(key: string, points: number) {
    setRules((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const next: ScoringRule = { ...r, max_points: points };
        if (r.sub_scores) {
          next.sub_scores = clampSubScores(r.sub_scores, points);
        }
        return next;
      }),
    );
  }

  function updateSubScore(ruleKey: string, originKey: string, points: number) {
    setRules((prev) =>
      prev.map((r) => {
        if (r.key !== ruleKey) return r;
        const max = r.max_points;
        const clamped = Math.max(0, Math.min(max, Math.round(points)));
        return {
          ...r,
          sub_scores: { ...(r.sub_scores ?? {}), [originKey]: clamped },
        };
      }),
    );
  }

  function addSubScore(ruleKey: string, key: string, points: number) {
    setRules((prev) =>
      prev.map((r) => {
        if (r.key !== ruleKey) return r;
        const max = r.max_points;
        const clamped = Math.max(0, Math.min(max, Math.round(points)));
        return {
          ...r,
          sub_scores: { ...(r.sub_scores ?? {}), [key]: clamped },
        };
      }),
    );
  }

  function removeSubScore(ruleKey: string, key: string) {
    setRules((prev) =>
      prev.map((r) => {
        if (r.key !== ruleKey) return r;
        const next = { ...(r.sub_scores ?? {}) };
        delete next[key];
        return { ...r, sub_scores: next };
      }),
    );
  }

  function updateRuleConfig(ruleKey: string, next: Record<string, unknown>) {
    setRules((prev) =>
      prev.map((r) => (r.key === ruleKey ? { ...r, config: next } : r)),
    );
  }

  function autoBalance() {
    if (project.cfo_effective_scoring) return;
    if (rules.length === 0) return;
    if (total === 0) {
      // Even split.
      const each = Math.floor(100 / rules.length);
      const remainder = 100 - each * rules.length;
      setRules(
        rules.map((r, i) => ({
          ...r,
          max_points: each + (i < remainder ? 1 : 0),
        })),
      );
      return;
    }
    // Proportional rescale to 100, then fix rounding error on the largest rule.
    const scaled = rules.map((r) => (r.max_points / total) * 100);
    const rounded = scaled.map((v) => Math.round(v));
    const sum = rounded.reduce((a, b) => a + b, 0);
    const drift = 100 - sum;
    if (drift !== 0) {
      let largestIdx = 0;
      let largestVal = -Infinity;
      for (let i = 0; i < rounded.length; i++) {
        if (rounded[i] > largestVal) {
          largestVal = rounded[i];
          largestIdx = i;
        }
      }
      rounded[largestIdx] = Math.max(0, rounded[largestIdx] + drift);
    }
    setRules(rules.map((r, i) => ({ ...r, max_points: rounded[i] })));
  }

  function reset() {
    setRules(initialRules);
    setThreshold(initialThreshold);
    setExclusions(initialExclusions);
    setScoringConfig(initialScoringConfig);
  }

  function removeExclusion(idx: number) {
    setExclusions((prev) => prev.filter((_, i) => i !== idx));
  }

  async function save() {
    setSubmitting(true);
    try {
      const payload = rules.map((r) => {
        const out: ScoringRule = {
          key: r.key,
          label: r.label,
          max_points: r.max_points,
        };
        if (r.sub_scores) {
          out.sub_scores = clampSubScores(r.sub_scores, r.max_points);
        }
        if (r.config) {
          out.config = r.config;
        }
        return out;
      });

      // scoring_rules / threshold / exclusions stay top-level (the
      // documented ProjectUpdate fields). The v2 scoring_config rides in
      // the free-form `config` patch — and ONLY when the operator
      // actually edited it. Two reasons:
      //   1. scoring_config is resolved from the project-type DEFAULT
      //      when there's no override, so sending it on every dirty save
      //      would snapshot that default into a hard per-project
      //      override, freezing the project off future default changes.
      //   2. We send only the `scoring_config` key (not a spread of
      //      project.config): the backend shallow-merges top-level config
      //      keys, so siblings (search_criteria, …) are preserved, and we
      //      avoid re-sending stale scoring_* copies that some projects
      //      also keep under config (which could clobber the new
      //      top-level values on write).
      const scoringConfigChanged =
        JSON.stringify(scoringConfig) !==
        JSON.stringify(initialScoringConfig);
      if (project.cfo_effective_scoring) {
        await updateCfoConfiguration(project.slug, {
          expected_fingerprint: project.cfo_effective_scoring.fingerprint,
          values: {
            scoring_rules: rules.map((r) => ({
              ...project.cfo_effective_scoring!.scoring_rules.find((original) => original.key === r.key),
              key: r.key, label: r.label, weight: r.max_points,
              ...(r.enabled !== undefined ? { enabled: r.enabled } : {}),
            })),
            scoring_threshold: threshold,
            scoring_exclusions: exclusions,
            ...(scoringConfigChanged ? { scoring_config: scoringConfig } : {}),
          },
        });
        const refreshed = await getProject(project.slug);
        setCurrentProject(refreshed);
        setProjects(projects.map((p) => p.slug === refreshed.slug ? refreshed : p));
        toast.success("CFO configuration saved. Required local reevaluations are queued.");
        return;
      }
      const updated = await updateProject(project.slug, {
        scoring_rules: payload,
        scoring_threshold: threshold,
        // Send the exclusion objects back verbatim so v2 fields (label,
        // value, enabled) aren't dropped — they were preserved in state.
        scoring_exclusions: exclusions,
        ...(scoringConfigChanged
          ? { config: { scoring_config: scoringConfig } }
          : {}),
      });
      setCurrentProject(updated);
      setProjects(
        projects.map((p) => (p.slug === updated.slug ? updated : p)),
      );
      setRescoreDialogOpen(true);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function rescoreNow() {
    setRescoring(true);
    try {
      const res = await rescoreAllCompanies(project.slug);
      setDirty(project.slug, false);
      toast.success(
        `Rescore started for ${res.total_companies.toLocaleString()} companies. Scores will update shortly.`,
      );
      setRescoreDialogOpen(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setRescoring(false);
    }
  }

  function rescoreLater() {
    setDirty(project.slug, true);
    toast.success(
      "Rules saved. Existing scores are unchanged until you recalculate.",
    );
    setRescoreDialogOpen(false);
  }

  const totalIsValid = total === 100;

  return (
    <section className="space-y-5">
      {project.cfo_effective_scoring && <CfoPolicyEditor project={project} canEdit={canEditScoring} />}
      {project.cfo_effective_scoring && <div className="rounded border p-3 space-y-2">
        <p>Saved canonical score ceiling: {project.cfo_effective_scoring.score_ceiling ?? "Legacy profile; extension not enabled"}. Weight and tier changes are validated together by the server.</p>
        {!rules.some((r) => r.key === "documented_founder_path") && <Button type="button" variant="outline" disabled={!canEditScoring || submitting} onClick={() => {
          setRules((previous) => [...previous, { key: "documented_founder_path", label: "Documented founder career", max_points: 0 }]);
          updateDimensionConfig("documented_founder_path", { enabled: false });
          setExpandedKey("documented_founder_path");
        }}>Add documented career component</Button>}
      </div>}
      <header>
        <h2 className="text-lg font-medium tracking-tight text-slate-900">
          Scoring
        </h2>
        <p className="text-sm text-slate-500">
          How the engine ranks companies. Total must equal 100.
        </p>
      </header>

      {!totalIsValid && (
        <div
          className={cn(
            "flex items-center justify-between rounded-plane border px-5 py-4",
            total > 100
              ? "border-rose-200 bg-rose-50/60"
              : "border-amber-200 bg-amber-50/60",
          )}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className={cn(
                "mt-0.5 h-4 w-4 shrink-0",
                total > 100 ? "text-rose-600" : "text-amber-600",
              )}
            />
            <div>
              <p
                className={cn(
                  "text-sm font-medium",
                  total > 100 ? "text-rose-700" : "text-amber-700",
                )}
              >
                {total} / 100 points
              </p>
              <p
                className={cn(
                  "text-xs",
                  total > 100 ? "text-rose-600" : "text-amber-600",
                )}
              >
                {total < 100
                  ? `Total is below 100 points (${total}). Some scoring potential unused.`
                  : `Total exceeds 100 points (${total}). Adjust rule weights.`}
              </p>
            </div>
          </div>
          {canEditScoring && !project.cfo_effective_scoring && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={autoBalance}
              className={cn(
                "bg-white",
                total > 100
                  ? "border-rose-200 text-rose-700 hover:bg-rose-100"
                  : "border-amber-200 text-amber-700 hover:bg-amber-100",
              )}
            >
              <Wand2 className="mr-2 h-3.5 w-3.5" />
              Auto-balance
            </Button>
          )}
        </div>
      )}

      <fieldset
        disabled={!canEditScoring}
        className="grid grid-cols-1 gap-5 disabled:opacity-60 lg:grid-cols-3">
        {/* Rules */}
        <div className="rounded-plane bg-white p-6 shadow-plane lg:col-span-2">
          <div className="mb-5 flex items-baseline justify-between">
            <h3 className="text-sm font-medium tracking-tight text-slate-900">
              Scoring rules
            </h3>
            <span
              className={cn(
                "text-xs font-medium tabular-nums",
                totalIsValid
                  ? "text-emerald-700"
                  : total > 100
                    ? "text-rose-700"
                    : "text-amber-700",
              )}
            >
              {total} / 100
            </span>
          </div>

          {rules.length === 0 ? (
            <p className="text-sm text-slate-400">
              No scoring rules defined for this project type.
            </p>
          ) : (
            <ul className="space-y-5">
              {rules.map((rule) => {
                const hasSubScores = SUB_SCORED_RULE_KEYS.has(rule.key);
                const hasConfig = !!rule.config;
                // CFO v2: a dimension's point breakdown lives in
                // scoring_config[rule.key] (a separate object from the
                // M&A `rule.config`), so it nests in the same expandable
                // panel under the weight slider.
                const dimConfig = scoringConfig[rule.key];
                const hasDimensionConfig =
                  !hasConfig &&
                  !hasSubScores &&
                  typeof dimConfig === "object" &&
                  dimConfig !== null &&
                  !Array.isArray(dimConfig);
                const isExpandable =
                  hasSubScores || hasConfig || hasDimensionConfig;
                const isExpanded = isExpandable && expandedKey === rule.key;
                const ruleConfig = (rule.config ?? {}) as Record<
                  string,
                  unknown
                >;
                const maxPossible = hasConfig
                  ? computeMaxPossibleScore(ruleConfig)
                  : null;
                const trancheCount = hasConfig
                  ? countTrancheCriteria(ruleConfig)
                  : 0;
                const overMax =
                  maxPossible !== null && maxPossible > rule.max_points;
                return (
                  <li key={rule.key} className="space-y-1.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          isExpandable &&
                          setExpandedKey(isExpanded ? null : rule.key)
                        }
                        className={cn(
                          "group flex min-w-0 flex-1 items-baseline gap-2 text-left",
                          isExpandable
                            ? "cursor-pointer"
                            : "cursor-default",
                        )}
                        aria-expanded={isExpandable ? isExpanded : undefined}
                      >
                        <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-sm font-medium text-slate-900">
                          {rule.label}
                          {isExpandable && (
                            <ChevronDown
                              className={cn(
                                "h-3.5 w-3.5 text-slate-400 transition-transform",
                                isExpanded && "rotate-180",
                              )}
                            />
                          )}
                          {overMax && (
                            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-normal text-amber-700">
                              <AlertTriangle className="h-3 w-3" />
                              Sub-scores total ({maxPossible}) exceeds max (
                              {rule.max_points})
                            </span>
                          )}
                        </div>
                      </button>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        aria-label={project.cfo_effective_scoring ? `Weight ${rule.key}` : undefined}
                        disabled={!!project.cfo_effective_scoring && rule.enabled === false}
                        value={rule.max_points}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (!Number.isFinite(n)) return;
                          updateRulePoints(
                            rule.key,
                            Math.max(0, Math.min(100, Math.round(n))),
                          );
                        }}
                        className={cn(
                          "h-9 w-20 rounded-lg border-slate-200 text-right font-mono",
                          overMax && "text-amber-600",
                        )}
                      />
                    </div>
                    {project.cfo_effective_scoring && rule.key === "fte_growth" && (
                      <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input type="checkbox" aria-label="FTE Growth rule enabled"
                          checked={rule.enabled !== false}
                          onChange={(event) => setRules((previous) => previous.map((item) =>
                            item.key === rule.key ? { ...item, enabled: event.target.checked,
                              max_points: event.target.checked ? item.max_points : 0 } : item))}
                        />
                        {rule.enabled === false ? "Rule disabled — growth is not required for scoring" : "FTE Growth rule enabled"}
                      </label>
                    )}
                    <Slider
                      disabled={!!project.cfo_effective_scoring && rule.enabled === false}
                      value={[rule.max_points]}
                      min={0}
                      max={50}
                      step={1}
                      onValueChange={(arr) =>
                        updateRulePoints(rule.key, arr[0])
                      }
                    />

                    {hasConfig && maxPossible !== null && (
                      <p className="text-[11px] text-slate-400">
                        Max possible: {maxPossible} pts from {trancheCount}{" "}
                        criteria
                        {!overMax && maxPossible !== rule.max_points && (
                          <span className="ml-2 text-slate-300">
                            (capped at {rule.max_points})
                          </span>
                        )}
                      </p>
                    )}

                    {isExpanded && hasConfig && (
                      <TrancheConfigEditor
                        config={rule.config as Record<string, unknown>}
                        onChange={(next) =>
                          updateRuleConfig(rule.key, next)
                        }
                      />
                    )}

                    {isExpanded && hasSubScores && !hasConfig &&
                      (rule.key === HIRING_SIGNALS_RULE_KEY ? (
                        <SubScoresEditor
                          max={rule.max_points}
                          subScores={rule.sub_scores ?? {}}
                          onUpdate={(key, points) =>
                            updateSubScore(rule.key, key, points)
                          }
                          onAdd={(key, points) =>
                            addSubScore(rule.key, key, points)
                          }
                          onRemove={(key) => removeSubScore(rule.key, key)}
                          allowAddRemove
                        />
                      ) : (
                        <FounderOriginSubScores
                          max={rule.max_points}
                          subScores={rule.sub_scores ?? {}}
                          onChange={(originKey, points) =>
                            updateSubScore(rule.key, originKey, points)
                          }
                        />
                      ))}

                    {isExpanded && hasDimensionConfig && (rule.key === "documented_founder_path" && project.cfo_effective_scoring ? (
                      <DocumentedPathEditor config={dimConfig as Record<string, unknown>} disabled={!canEditScoring} onChange={(next) => updateDimensionConfig(rule.key, next)} />
                    ) : (
                      <DimensionConfigEditor
                        config={dimConfig as Record<string, unknown>}
                        maxPoints={project.cfo_effective_scoring ? undefined : rule.max_points}
                        disabled={!canEditScoring}
                        onChange={(next) =>
                          updateDimensionConfig(rule.key, next)
                        }
                      />
                    ))}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Threshold + exclusions */}
        <div className="space-y-5">
          <div className="rounded-plane bg-white p-6 shadow-plane">
            <h3 className="text-sm font-medium tracking-tight text-slate-900">
              {project.cfo_effective_scoring ? "Qualification threshold" : "Contact threshold"}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {project.cfo_effective_scoring ? "Effective qualification threshold. Required evidence and do-not-contact checks remain mandatory; no commercial sending is triggered." : "Minimum score to trigger an outreach."}
            </p>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-xs text-slate-400">Cold</span>
              <Slider
                value={[threshold]}
                min={0}
                max={100}
                step={1}
                onValueChange={(arr) => setThreshold(arr[0])}
                className="flex-1"
              />
              <span className="text-xs text-slate-400">Hot</span>
            </div>
            <div className="mt-2 flex items-center justify-end">
              <Input
                type="number"
                min={0}
                max={100}
                aria-label={project.cfo_effective_scoring ? "CFO qualification threshold" : undefined}
                value={threshold}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (!Number.isFinite(n)) return;
                  setThreshold(Math.max(0, Math.min(100, Math.round(n))));
                }}
                className="h-9 w-20 rounded-lg border-slate-200 text-right font-mono"
              />
            </div>
          </div>

          <div className="rounded-plane bg-white p-6 shadow-plane">
            <h3 className="text-sm font-medium tracking-tight text-slate-900">
              Excluding filters
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Companies matching these conditions get a score of 0.
            </p>
            {exclusions.length === 0 ? (
              <p className="mt-4 text-sm text-slate-400">
                No exclusions configured.
              </p>
            ) : (
              <ul className="mt-4 space-y-2">
                {exclusions.map((ex, idx) => {
                  // Default to enabled unless the row explicitly carries
                  // `enabled: false` (v1 rows have no `enabled` field).
                  const enabled = ex.enabled !== false;
                  const label = exclusionDisplay(ex);
                  return (
                    <li
                      key={idx}
                      className={cn(
                        "flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2.5",
                        !enabled && "opacity-60",
                      )}
                    >
                      <div className="min-w-0 flex-1 text-sm">
                        <span
                          className={cn(
                            "font-medium text-slate-900",
                            !enabled &&
                              "text-slate-400 line-through decoration-slate-300",
                          )}
                        >
                          {label}
                        </span>
                        {!enabled && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                            disabled
                          </span>
                        )}
                      </div>
                      {canEditScoring && (
                        <button
                          type="button"
                          onClick={() => removeExclusion(idx)}
                          className="text-slate-400 hover:text-rose-600"
                          aria-label={`Remove ${label} exclusion`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </fieldset>

      {/* CFO v2 advanced config — keyword lists, booster + stage-duration
       *  knobs. Rendered outside the fieldset so read-only operators can
       *  still expand the collapsibles to inspect values; the inputs
       *  inside carry their own `disabled` state. Returns null for
       *  project types without a scoring_config. */}
      <GlobalScoringConfig
        config={scoringConfig}
        onChange={setScoringConfig}
        disabled={!canEditScoring}
      />

      {canEditScoring && (
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={reset}
            disabled={!isDirty || submitting}
          >
            Reset
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={!isDirty || submitting || !totalIsValid}
          variant="primary"
          >
            {submitting && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Save changes
          </Button>
        </div>
      )}

      <Dialog
        open={rescoreDialogOpen}
        onOpenChange={(o) => {
          if (rescoring) return;
          if (!o) rescoreLater();
        }}
      >
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Recalculate scores?</DialogTitle>
            <DialogDescription>
              The scoring rules have been updated. Do you want to recalculate
              the scores for all companies in this project?
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 text-xs text-slate-600">
            Existing scores are based on the previous rules until you
            recalculate. New companies created from now on will use the new
            rules automatically.
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={rescoreLater}
              disabled={rescoring}
            >
              Later
            </Button>
            <Button
              type="button"
              onClick={rescoreNow}
              disabled={rescoring}
            variant="primary"
            >
              {rescoring ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-3.5 w-3.5" />
              )}
              Recalculate now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't save scoring.";
}

function clampSubScores(
  scores: Record<string, number>,
  max: number,
): Record<string, number> {
  const next: Record<string, number> = {};
  for (const [k, v] of Object.entries(scores)) {
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    next[k] = Math.max(0, Math.min(max, Math.round(n)));
  }
  return next;
}

function subScoresEqual(
  a: Record<string, number> | null | undefined,
  b: Record<string, number> | null | undefined,
): boolean {
  if (!a && !b) return true;
  const ak = Object.keys(a ?? {});
  const bk = Object.keys(b ?? {});
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if ((a ?? {})[k] !== (b ?? {})[k]) return false;
  }
  return true;
}

interface FounderOriginSubScoresProps {
  max: number;
  subScores: Record<string, number>;
  onChange: (originKey: string, points: number) => void;
}

function FounderOriginSubScores({
  max,
  subScores,
  onChange,
}: FounderOriginSubScoresProps) {
  return (
    <div className="mt-3 rounded-xl border border-slate-900/[0.06] bg-slate-50/40 p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Per-origin breakdown
        </p>
        <p className="text-[11px] text-slate-400">
          0 –{" "}
          <span className="tabular-nums font-mono text-slate-600">{max}</span>{" "}
          pts
        </p>
      </div>
      <ul className="space-y-1.5">
        {FOUNDER_ORIGIN_FLAT.map((opt) => {
          const excluded = !!opt.excluded;
          const value = subScores[opt.value] ?? 0;
          return (
            <li
              key={opt.value}
              className={cn(
                "flex items-center justify-between gap-3 rounded-md px-2 py-1.5",
                excluded && "bg-rose-50/40",
              )}
            >
              <span
                className={cn(
                  "min-w-0 truncate text-sm",
                  excluded ? "text-rose-700" : "text-slate-700",
                )}
              >
                {opt.label}
                {excluded && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-rose-500">
                    excluded
                  </span>
                )}
              </span>
              {excluded ? (
                <span className="font-mono text-xs text-rose-400">—</span>
              ) : (
                <Input
                  type="number"
                  min={0}
                  max={max}
                  value={value}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (!Number.isFinite(n)) return;
                    onChange(opt.value, n);
                  }}
                  className="h-8 w-16 rounded-lg border-slate-200 text-right font-mono text-xs"
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
